import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';
import { Ajax2 } from '@shared/Ajax';
import { setMapCursor } from '@openlayers/ol-utils';
import Feature from 'ol/Feature';
import { Events } from '@shared/Events';
import { transform as olTransform } from 'ol/proj';
import { ModelService } from '@shared/models/model-service';
import { RateLimiter } from '@shared/utils/rate-limiter';
import { Auth } from '@shared/Auth';


const handleStructure = (structure, props) => {
    if (structure.structures) {
        structure.structures.forEach(child => {
            const childProps = handleStructure(child, props)
            props = {...props, ...childProps}
        } )
    }

    if (structure.props) {
        props = {...props, ...structure.props}
    }

    return props;
}

const rateLimiter = new RateLimiter(1, 10);


const getModelPickAtLocation = async (fileUuid: string, lon: number, lat: number) => {
    const vizApiBaseUrl = ModelService.getVizApiBaseUrl();

    const pickUrl = `${vizApiBaseUrl}/${kuura.getProjectUuid()}/${fileUuid}/pick?longitude=${lon}&latitude=${lat}`;


    const returnToken = await rateLimiter.requestToken();

    const authToken = await Auth.getToken();

    const pickResp: Response = await fetch(pickUrl, {
        headers: {
            'Authorization': `Bearer ${authToken.accessToken}`
        }
    });

    if (!pickResp.ok) {
        returnToken();
        return null;
    }

    const respData = await pickResp.json();

    returnToken();
    return respData;
}


export class ModelPropMouseInteraction extends FeatureMouseInteraction {

    isLoading = false;

    constructor(map, private opts: {flipClicks?: boolean} = {}) {
        super(map);
    }

    private async pickFeature (feature: Feature, e: number, n: number) {
        const fileUuid = feature.get('fileUuid');
        const [lon, lat] = olTransform([e,n], 'EPSG:3857', 'EPSG:4326');
        const respData = await getModelPickAtLocation(fileUuid, lon, lat);

        return respData;
    }

    private async handleClick (feature: Feature, e, n, featuresAtPixel: Feature[] = [feature]) {
        this.isLoading = true;
        document.body.style.cursor = 'wait';
        let fileUuid = feature.get('fileUuid');
        let fileName = feature.get('fileName');

        let respData = null;

        for (const featureAtPixel of featuresAtPixel) {
            const featureRespData = await this.pickFeature(featureAtPixel, e, n);

            if (featureRespData) {
                fileUuid = featureAtPixel.get('fileUuid');
                fileName = featureAtPixel.get('fileName');
                respData = featureRespData;
                break;
            }
        }

        if (!respData) {
            document.body.style.cursor = '';
            this.isLoading = false;
            Events.trigger('mapClickModel', {
                id: null,
                name: null,
                props: null,
                isStructure: false
            });
            return;
        }

        const propResp = await Ajax2({
            url: kuura.getContextPath() + '/api/1/model/' + fileUuid + '/structure/properties?structName=' + respData.properties.name,
            contentType: 'json',
            method: 'GET',
            useToken: true
        });

        if (!propResp.status) {
            document.body.style.cursor = '';
            this.isLoading = false;
            return;
        }

        const props = handleStructure(propResp.structures[0], {});
        const propData = {
            id: respData.id,
            name: `${fileName}: ${respData.properties.name}`,
            props: props,
            isStructure: true,
        }

        Events.trigger('mapClickModel', propData);
        document.body.style.cursor = '';
        this.isLoading = false;
    }

    onClick = (feature: Feature, e, n, featuresAtPixel: Feature[] = [feature]): void => {
        if (this.opts.flipClicks) {
            return;
        }
        if (this.isLoading) {
            return;
        }
        setMapCursor(this.map, '');
        this.handleClick(feature, e, n, featuresAtPixel);
    };

    onRightClick = (feature: Feature, e, n, featuresAtPixel: Feature[] = [feature]): void => {
        if (!this.opts.flipClicks) {
            return;
        }
        if (this.isLoading) {
            return;
        }
        setMapCursor(this.map, '');
        this.handleClick(feature, e, n, featuresAtPixel);
    };

    onMouseOver = (feature: Feature, e, n): void => {
        if (this.isLoading) {
            return;
        }
        setMapCursor(this.map, 'pointer');
    };

}
